/**
 * LINE 文字訊息長度估算
 *
 * 編輯器顯示的字數是「含 {{變數}} 的原始文字」，但實際送到 LINE 的是替換後的結果，
 * 因此需要用各變數可能的最大長度回推「最壞情況長度」，
 * 避免存檔時看起來安全、實際發送時超過 LINE 的 5000 字元上限。
 *
 * 變數清單須與後端 api/src/utils/lineMsgTemplateVariables.js 一致。
 */

/** LINE 文字訊息上限 */
export const LINE_TEXT_MAX_LENGTH = 5000;

/** 最壞情況長度達此值就提醒「接近滿載」 */
export const LINE_TEXT_WARN_LENGTH = 4500;

/**
 * 各變數替換後的最大長度，取資料庫欄位長度為保守上限：
 * members.name varchar(100)、members.member_code varchar(50)、
 * line_follow.line_display_name varchar(255)、member_tiers.tier_name varchar(100)、
 * members.member_tier varchar(20)、整數／金額欄位以 11 位計、日期為 zh-TW 格式 10 字
 */
const VARIABLE_MAX_LENGTHS = {
  memberName: 100,
  name: 100,
  lineName: 255,
  memberCode: 50,
  points: 11,
  availablePoints: 11,
  totalPoints: 11,
  pointsAmount: 11,
  couponCount: 11,
  memberTier: 20,
  memberTierName: 100,
  tierName: 100,
  totalSpent: 11,
  purchaseCount: 11,
  lastPurchaseDate: 10,
  registrationDate: 10
};

/** 編輯器的 {{$1}} 表情佔位存檔時會換成單一 $，長度會縮短 */
function toApiText(editorText) {
  return String(editorText || '').replace(/\{\{\$\d+\}\}/g, '$');
}

function countOccurrences(text, token) {
  let count = 0;
  let from = 0;
  for (;;) {
    const at = text.indexOf(token, from);
    if (at === -1) break;
    count += 1;
    from = at + token.length;
  }
  return count;
}

/**
 * @param {string} editorText 編輯器中的原始文字（含 {{變數}} 與 {{$n}} 表情佔位）
 * @returns {{
 *   apiLength: number,
 *   worstCaseLength: number,
 *   variables: Array<{ key: string, count: number, maxLength: number }>
 * }}
 */
export function estimateLineTextLength(editorText) {
  const apiText = toApiText(editorText);
  const variables = [];
  let worstCase = apiText.length;

  for (const [key, maxLength] of Object.entries(VARIABLE_MAX_LENGTHS)) {
    const token = `{{${key}}}`;
    const count = countOccurrences(apiText, token);
    if (count === 0) continue;
    variables.push({ key, count, maxLength });
    worstCase += count * (maxLength - token.length);
  }

  return {
    apiLength: apiText.length,
    // 群發範本不替換變數，原文長度本身就是下限
    worstCaseLength: Math.max(apiText.length, worstCase),
    variables
  };
}
